/**
 * Central SEO configuration.
 *
 * Everything user-visible to a search engine (titles, descriptions, canonical
 * URLs, structured data) is derived from this file so the metadata stays
 * consistent between the runtime <Seo /> component, the HTML sitemap page and
 * the sitemap.xml generator script.
 */

// Canonical origin, no trailing slash. Must be the host that serves a 200 —
// https://www.orucom.com 301-redirects to the apex, so the apex is canonical.
// Override per-environment with REACT_APP_SITE_URL so preview deploys don't
// emit production canonicals.
export const SITE_URL = (
  process.env.REACT_APP_SITE_URL || "https://orucom.com"
).replace(/\/+$/, "");

export const SITE_NAME = "Orucom";
export const SITE_TAGLINE = "Book Beauty Salons & Spas Online";
export const TITLE_TEMPLATE = (title) =>
  title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — ${SITE_TAGLINE}`;

// Keep every indexable description in the 70-160 character window — Google
// truncates the snippet past roughly 160.
export const DEFAULT_DESCRIPTION =
  "Book appointments at verified beauty salons and spas near you. Compare hair, skin, nail and bridal services, check real ratings and book in seconds — free.";

// TODO: drop a purpose-made 1200x630 share card at public/og-image.jpg and
// point this at it — logo512.png works but renders as a small square in
// Facebook/WhatsApp/X previews rather than a full-width banner.
export const DEFAULT_OG_IMAGE = `${SITE_URL}/logo512.png`;
export const LOGO_URL = `${SITE_URL}/logo512.png`;
export const TWITTER_HANDLE = "@orucom";
export const CONTACT_EMAIL = "nominoinnovations@gmail.com";
export const ANDROID_APP_URL =
  "https://play.google.com/store/apps/details?id=com.orucom.customer";

export const ORGANIZATION = {
  legalName: "Orucom Beauty Platform",
  areaServed: "Kerala, India",
  addressRegion: "Kerala",
  addressCountry: "IN",
};

/** Turn a route path into an absolute, canonical URL. */
export const absoluteUrl = (path = "/") => {
  if (!path) return SITE_URL;
  if (/^https?:\/\//i.test(path)) return path;
  const clean = `/${String(path).replace(/^\/+/, "")}`;
  // Strip the trailing slash on everything except the homepage.
  return clean === "/" ? `${SITE_URL}/` : `${SITE_URL}${clean.replace(/\/+$/, "")}`;
};

/** Section order on the /sitemap page. */
export const HTML_SITEMAP_GROUPS = [
  "Discover",
  "About Orucom",
  "For Salon Owners",
  "Your Account",
  "Legal",
];

/**
 * Per-route metadata.
 *
 * `path`        canonical path for the route
 * `changefreq`  / `priority`  used by the sitemap.xml generator
 * `noindex`     private / transactional pages kept out of the index
 * `inSitemap`   belongs in sitemap.xml — indexable pages only
 * `htmlGroup`   which section of the /sitemap page it appears under. Set for
 *               every page a person can reach, including logged-in ones: that
 *               page serves humans as well as crawlers. Leave null for
 *               mid-booking steps and admin, which nobody navigates to
 *               directly.
 * `requiresLogin`  shown as a hint on the /sitemap page
 */
export const PAGES = {
  home: {
    path: "/",
    title: null, // uses the "Orucom — Book Beauty Salons & Spas Online" form
    description: DEFAULT_DESCRIPTION,
    keywords:
      "beauty salon booking, salon near me, spa booking, hair salon Kerala, bridal makeup, book salon appointment online",
    changefreq: "daily",
    priority: 1.0,
    inSitemap: true,
    htmlGroup: "Discover",
    label: "Home",
  },

  shops: {
    path: "/shops",
    title: "All Beauty Salons & Spas",
    description:
      "Browse every verified beauty salon and spa on Orucom. Filter by hair, makeup, nails, skin, bridal and massage, sort by rating and book instantly.",
    keywords:
      "beauty salons list, spa directory, top rated salons, salon reviews, book salon online",
    changefreq: "daily",
    priority: 0.9,
    inSitemap: true,
    htmlGroup: "Discover",
    label: "All Salons",
  },

  nearby: {
    path: "/nearby",
    title: "Beauty Salons Near Me",
    description:
      "Find top-rated beauty salons and spas near your current location. See distance, opening hours, services and ratings, then book an appointment in seconds.",
    keywords:
      "salon near me, beauty parlour near me, spa near me, nearby hair salon, local beauty salon",
    changefreq: "daily",
    priority: 0.9,
    inSitemap: true,
    htmlGroup: "Discover",
    label: "Salons Near Me",
  },

  aboutUs: {
    path: "/about-us",
    title: "About Us",
    description:
      "Orucom is a Kerala-based beauty booking platform connecting customers with verified salons and spas. Learn who we are and how we vet every salon we list.",
    keywords:
      "about orucom, orucom beauty platform, verified salons Kerala, beauty booking company",
    changefreq: "monthly",
    priority: 0.7,
    inSitemap: true,
    htmlGroup: "About Orucom",
    label: "About Us",
  },

  sitemap: {
    path: "/sitemap",
    title: "Sitemap",
    description:
      "A complete map of Orucom — every section, salon listing and policy page in one place, so you can find exactly what you need.",
    keywords: "orucom sitemap, site map, all pages",
    changefreq: "weekly",
    priority: 0.4,
    inSitemap: true,
    htmlGroup: "About Orucom",
    label: "Sitemap",
  },

  contact: {
    path: "/contact",
    title: "Contact & Support",
    description:
      "Need help with a booking or want to list your salon on Orucom? Reach the Orucom support team and browse answers to the most common questions.",
    keywords: "orucom support, contact orucom, list my salon, salon partner help",
    changefreq: "monthly",
    priority: 0.6,
    inSitemap: true,
    htmlGroup: "About Orucom",
    label: "Contact & Support",
  },

  // /support and /help render the same screen as /contact. They keep their own
  // titles but canonicalise to /contact so Google never sees three duplicates.
  support: {
    path: "/support",
    canonical: "/contact",
    title: "Support",
    description:
      "Need help with a booking or want to list your salon on Orucom? Reach the Orucom support team and browse answers to the most common questions.",
    inSitemap: false,
  },
  help: {
    path: "/help",
    canonical: "/contact",
    title: "Help Centre",
    description:
      "Need help with a booking or want to list your salon on Orucom? Reach the Orucom support team and browse answers to the most common questions.",
    inSitemap: false,
  },

  signin: {
    path: "/signin",
    title: "Sign In",
    description:
      "Sign in to your Orucom account to manage bookings, view your wishlist and rebook your favourite salons.",
    noindex: true,
    inSitemap: false,
    htmlGroup: "Your Account",
    label: "Sign In",
  },

  signup: {
    path: "/signup",
    title: "List Your Salon — Partner With Orucom",
    description:
      "Grow your beauty business with Orucom. List your salon or spa free, manage bookings from one dashboard and reach thousands of customers near you.",
    keywords:
      "list my salon, salon partner program, beauty business software, salon booking system, register salon online",
    changefreq: "monthly",
    priority: 0.8,
    inSitemap: true,
    htmlGroup: "For Salon Owners",
    label: "List Your Salon",
  },

  customerSignup: {
    path: "/signup/type=customer",
    title: "Create Your Customer Account",
    description:
      "Create a free Orucom account to book beauty appointments, save favourite salons and track your booking history.",
    noindex: true,
    inSitemap: false,
    htmlGroup: "Your Account",
    label: "Create an Account",
  },

  privacy: {
    path: "/privacy",
    title: "Privacy Policy",
    description:
      "How Orucom collects, uses, stores and protects your personal data, and the choices and rights you have over your information.",
    changefreq: "yearly",
    priority: 0.3,
    inSitemap: true,
    htmlGroup: "Legal",
    label: "Privacy Policy",
  },

  terms: {
    path: "/terms",
    title: "Terms & Conditions",
    description:
      "The terms and conditions that govern your use of the Orucom platform, bookings made through it and the responsibilities of customers and partner salons.",
    changefreq: "yearly",
    priority: 0.3,
    inSitemap: true,
    htmlGroup: "Legal",
    label: "Terms & Conditions",
  },

  cookies: {
    path: "/cookies",
    title: "Cookie Policy",
    description:
      "Which cookies and similar technologies Orucom uses, what each one does and how you can control them in your browser.",
    changefreq: "yearly",
    priority: 0.3,
    inSitemap: true,
    htmlGroup: "Legal",
    label: "Cookie Policy",
  },

  notFound: {
    path: "/404",
    title: "Page Not Found",
    description:
      "The page you were looking for doesn't exist or has moved. Browse verified beauty salons on Orucom instead.",
    noindex: true,
    inSitemap: false,
  },

  // ── Private / transactional. Indexed content here would be thin or
  // user-specific, so every one of these is explicitly noindex. ──
  summary: {
    path: "/summary",
    title: "Booking Summary",
    description: "Review and confirm your salon booking.",
    noindex: true,
    inSitemap: false,
  },
  serviceListing: {
    title: "Salon Services",
    description: "Choose a service to book at this salon.",
    noindex: true,
    inSitemap: false,
  },
  slotSelection: {
    title: "Choose Your Appointment Slot",
    description: "Pick a date and time for your salon appointment.",
    noindex: true,
    inSitemap: false,
  },
  myBookings: {
    path: "/my-bookings",
    title: "My Bookings",
    description: "View, reschedule or cancel your upcoming salon appointments.",
    noindex: true,
    inSitemap: false,
    htmlGroup: "Your Account",
    label: "My Bookings",
    requiresLogin: true,
  },
  wishlist: {
    path: "/wishlist",
    title: "My Wishlist",
    description: "The salons you've saved for later.",
    noindex: true,
    inSitemap: false,
    htmlGroup: "Your Account",
    label: "My Wishlist",
    requiresLogin: true,
  },
  history: {
    path: "/history",
    title: "Booking History",
    description: "A record of every appointment you've booked through Orucom.",
    noindex: true,
    inSitemap: false,
    htmlGroup: "Your Account",
    label: "Booking History",
    requiresLogin: true,
  },
  settings: {
    path: "/settings",
    title: "Account Settings",
    description: "Manage your Orucom account preferences and notifications.",
    noindex: true,
    inSitemap: false,
    htmlGroup: "Your Account",
    label: "Account Settings",
    requiresLogin: true,
  },
  shopDashboard: {
    path: "/shop/dashboard",
    title: "Salon Dashboard",
    description: "Manage your salon's bookings, services and team in one place.",
    noindex: true,
    inSitemap: false,
    htmlGroup: "For Salon Owners",
    label: "Salon Dashboard",
    requiresLogin: true,
  },
  shopProfile: {
    path: "/shop/profile",
    title: "Salon Profile",
    description: "Your salon's public Orucom profile.",
    noindex: true,
    inSitemap: false,
    htmlGroup: "For Salon Owners",
    label: "Salon Profile",
    requiresLogin: true,
  },
  shopEditProfile: {
    path: "/shop/edit-profile",
    title: "Edit Salon Profile",
    description: "Update your salon's details, services and gallery.",
    noindex: true,
    inSitemap: false,
    htmlGroup: "For Salon Owners",
    label: "Edit Salon Profile",
    requiresLogin: true,
  },
  shopOnboard: {
    path: "/shop/onboard",
    title: "Salon Onboarding",
    description: "Finish setting up your salon on Orucom.",
    noindex: true,
    inSitemap: false,
    htmlGroup: "For Salon Owners",
    label: "Salon Onboarding",
    requiresLogin: true,
  },
  adminLogin: {
    path: "/admin/login",
    title: "Admin Login",
    description: "Orucom administration access.",
    noindex: true,
    inSitemap: false,
  },
  adminDashboard: {
    path: "/admin/dashboard",
    title: "Admin Dashboard",
    description: "Orucom administration dashboard.",
    noindex: true,
    inSitemap: false,
  },
};

/** Indexable routes — these go in sitemap.xml. */
export const SITEMAP_PAGES = Object.values(PAGES).filter((p) => p.inSitemap);

/**
 * Every page listed on the /sitemap page, grouped and ordered for display.
 * Wider than SITEMAP_PAGES on purpose: the HTML sitemap is a navigation aid
 * for people too, so it includes the logged-in pages that sitemap.xml
 * (correctly) leaves out.
 */
export const HTML_SITEMAP_SECTIONS = HTML_SITEMAP_GROUPS.map((group) => ({
  group,
  pages: Object.values(PAGES).filter((p) => p.htmlGroup === group),
})).filter((section) => section.pages.length > 0);

/** Paths crawlers should never spend budget on. Mirrored in robots.txt. */
export const DISALLOWED_PATHS = [
  "/admin",
  "/summary",
  "/settings",
  "/my-bookings",
  "/wishlist",
  "/history",
  "/signin",
  "/shop/dashboard",
  "/shop/profile",
  "/shop/edit-profile",
  "/shop/onboard",
  "/parlor",
];
